import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { HlmBtnDirective, HlmIconComponent, HlmSwitchComponent } from '../../../ui';
import { BrandDomain } from './domain.model';

export interface BrandDomainGridContext {
  canWrite: () => boolean;
  toggling: () => boolean;
  toggleMail: (row: BrandDomain) => void;
  confirmDelete: (row: BrandDomain) => void;
}

/**
 * Renders ONE control depending on which column it sits in (read from `colId`):
 * - `mail`    → the mail-domain toggle
 * - `actions` → the Remove button
 * Keeping a single renderer avoids a second tiny component while letting the
 * toggle and the button live in their own columns (clean alignment, no clipping).
 */
@Component({
  selector: 'cmt-brand-domain-action-cell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmBtnDirective, HlmIconComponent, HlmSwitchComponent],
  template: `
    @if (mode === 'mail') {
      <div class="flex h-full items-center">
        <hlm-switch
          [checked]="row?.mailDomain ?? false"
          [disabled]="!ctx?.canWrite() || !!ctx?.toggling()"
          (checkedChange)="onToggleMail()"
          aria-label="Use as outgoing mail domain"
        />
      </div>
    } @else {
      <div class="flex h-full items-center">
        <button
          hlmBtn
          variant="destructive"
          size="sm"
          type="button"
          class="h-7 px-2.5"
          [disabled]="!ctx?.canWrite()"
          (click)="onDelete($event)"
          aria-label="Remove domain"
        >
          <hlm-icon name="lucideTrash2" size="0.9rem" />
          Remove
        </button>
      </div>
    }
  `,
})
export class BrandDomainActionCellComponent implements ICellRendererAngularComp {
  protected ctx: BrandDomainGridContext | null = null;
  protected row: BrandDomain | null = null;
  protected mode: 'mail' | 'actions' = 'actions';

  agInit(params: ICellRendererParams<BrandDomain>): void {
    this.apply(params);
  }

  refresh(params: ICellRendererParams<BrandDomain>): boolean {
    this.apply(params);
    return true;
  }

  private apply(params: ICellRendererParams<BrandDomain>): void {
    this.row = params.data ?? null;
    this.ctx = params.context as BrandDomainGridContext;
    this.mode = params.colDef?.colId === 'brand-domain-mail' ? 'mail' : 'actions';
  }

  protected onToggleMail(): void {
    if (this.row) this.ctx?.toggleMail(this.row);
  }

  protected onDelete(event: MouseEvent): void {
    event.stopPropagation();
    if (this.row && this.ctx) this.ctx.confirmDelete(this.row);
  }
}
